import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { isSubmitChord } from '../lib/shortcut';
import { CLOSE_KEYS, SUBMIT_KEYS } from './keycap';
import { Button } from './button';

export type TModalWidth = 'sm' | 'md' | 'lg' | 'wide';

const WIDTH: Record<TModalWidth, string> = {
  sm: 'w-modal',
  md: 'w-modal-md',
  lg: 'w-modal-lg',
  wide: 'w-modal-wide',
};

// Data, not a rendered button, so ONE `disabled` feeds both the button and ⌘↵.
export interface IModalSubmit {
  label: string;
  onSubmit: () => void;
  disabled?: boolean;
  waiting?: boolean;
  waitingLabel?: string;
  destructive?: boolean;
}

interface IModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  width?: TModalWidth;
  submit?: IModalSubmit;
  cancelLabel?: string;
  footer?: ReactNode;
  footerNote?: ReactNode;
  children?: ReactNode;
  bleed?: boolean;
  className?: string;
}

export const Modal = ({
  open,
  onOpenChange,
  title,
  description,
  width = 'sm',
  submit,
  cancelLabel = 'Cancel',
  footer,
  footerNote,
  children,
  bleed,
  className,
}: IModalProps) => {
  const blocked = Boolean(submit?.disabled || submit?.waiting);

  const hasFooter = Boolean(submit || footer || footerNote);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          // On the content, not the document: a modal behind this one must not
          // answer the chord.
          onKeyDown={(event) => {
            if (!submit || blocked || !isSubmitChord(event)) return;
            event.preventDefault();
            submit.onSubmit();
          }}
          className={cn(
            'fixed top-1/2 left-1/2 max-w-full -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-card border border-border-raised bg-surface-raised shadow-panel',
            WIDTH[width],
            className,
          )}
        >
          <div className={cn('px-4 pt-4', children ? undefined : 'pb-4')}>
            <Dialog.Title className="text-brand font-semibold text-primary">{title}</Dialog.Title>
            {description && <p className="mt-2 text-data text-muted">{description}</p>}
          </div>

          {children && <div className={bleed ? undefined : 'px-4 pt-1 pb-4'}>{children}</div>}

          {hasFooter && (
            <div className="flex items-center gap-inset border-t border-border-raised px-4 py-4">
              {footerNote && <span className="font-mono text-micro text-faint">{footerNote}</span>}
              <div className="ml-auto flex min-w-0 items-center gap-2">
                {footer}
                {submit && (
                  <>
                    {/* A plate, not a ghost: beside a filled primary a background-less
                        control reads as a shorter one, and the pair looked misaligned. */}
                    <Button
                      variant="secondary"
                      size="sm"
                      hint={CLOSE_KEYS}
                      onClick={() => onOpenChange(false)}
                    >
                      {cancelLabel}
                    </Button>
                    <Button
                      size="sm"
                      variant={submit.destructive ? 'secondary' : 'primary'}
                      destructive={submit.destructive}
                      hint={SUBMIT_KEYS}
                      disabled={submit.disabled}
                      waiting={submit.waiting}
                      waitingLabel={submit.waitingLabel}
                      onClick={submit.onSubmit}
                      className="min-w-0"
                    >
                      <span className="truncate">{submit.label}</span>
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
